/**
 * Build Ultimate Setup textarea seed text from Account Vault (Notes & Files).
 * Prefer the raw user paste stored in the Ultimate Setup audit note; otherwise
 * concatenate Brand / Audience / Offer / Goals entries.
 */

const CATEGORY_ORDER = { brand: 0, personas: 1, offer: 2, goals: 3, general: 9 };

/**
 * Extract raw user input from the markdown body of "Setup Notes" / "Setup-Notizen".
 */
export function extractRawInputFromSetupNotesMarkdown(md) {
  if (!md || typeof md !== 'string') return '';
  const marker = md.includes('## Raw user input')
    ? '## Raw user input'
    : md.includes('## Roh-Input des Users')
      ? '## Roh-Input des Users'
      : '';
  if (!marker) return '';
  const pos = md.indexOf(marker);
  const after = md.slice(pos + marker.length);
  const openIdx = after.indexOf('```');
  if (openIdx === -1) return '';
  const afterOpen = after.slice(openIdx + 3);
  const closeIdx = afterOpen.indexOf('```');
  if (closeIdx === -1) return '';
  let inner = afterOpen.slice(0, closeIdx).replace(/^\s*/, '').trim();
  if (inner === '_(empty)_' || inner === '') return '';
  return inner;
}

function isSetupAuditNote(entry) {
  return !!(entry?.tags && entry.tags.setup === true && entry.tags.raw === true);
}

/**
 * @param {Record<string, object>|null|undefined} entriesMap — RTDB dataVault/entries snap.val()
 * @returns {string} combined text for Ultimate Setup rawInfo (may be empty)
 */
export function buildVaultSeedRawInfo(entriesMap) {
  if (!entriesMap || typeof entriesMap !== 'object') return '';

  const entries = Object.values(entriesMap).filter((e) => e && typeof e === 'object');

  let fromNotes = '';
  for (const e of entries) {
    if (isSetupAuditNote(e) && typeof e.content === 'string') {
      fromNotes = extractRawInputFromSetupNotesMarkdown(e.content);
      if (fromNotes.length >= 50) break;
    }
  }

  const fallbackParts = [];
  const sorted = [...entries]
      .filter((e) => typeof e.content === 'string' && e.content.trim())
      .filter((e) => !isSetupAuditNote(e));

  sorted.sort(
      (a, b) => (CATEGORY_ORDER[a.category] ?? 99) - (CATEGORY_ORDER[b.category] ?? 99),
  );

  for (const e of sorted) {
    const title = (e.title || 'Note').trim();
    const body = e.content.trim();
    fallbackParts.push(`# ${title}\n${body}`);
  }
  const fallback = fallbackParts.join('\n\n---\n\n').trim();

  if (fromNotes.length >= 50) return fromNotes;
  if (fromNotes.length > 0 && fallback.length > 0) {
    return `${fromNotes}\n\n---\n\n${fallback}`;
  }
  if (fromNotes.length > 0) return fromNotes;
  return fallback;
}
